import React, { useEffect, useContext, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Card,
  Chip,
  Divider,
  Paper,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack,
  Assignment,
  Bookmark,
  BookmarkBorder,
  ContentCopy,
  Edit,
  ExpandMore,
  LogoutOutlined,
  PersonOutline,
  PlayArrow,
  School,
  ShoppingCart,
} from "@mui/icons-material";

// Argon Dashboard 2 MUI components
import Box from "themeComponents/Box";
import Typography from "themeComponents/Typography";
import Button from "themeComponents/Button";
import DashboardLayout from "themeComponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "themeComponents/Navbars/DashboardNavbar";
import Footer from "themeComponents/Footer";
import Loader from "components/loader";
import { DeleteAlert } from "components/swalAlert";
import { AppContext } from "context/appContext";
import { courseApi } from "configs/api";
import CourseAssignmentDialog from "./CourseAssignmentDialog";
import CoursePurchaseDialog from "./CoursePurchaseDialog";
import StripePaymentDialog from "./StripePaymentDialog";
import CourseStatusChip from "./components/CourseStatusChip";

// Hooks
import { useCourse } from "hooks/useCourse";
import { useDispatch } from "react-redux";
import { clearCurrentCourse, setCurrentCourse } from "slices/courseSlice";
import useAuth from "hooks/useAuth";
import useDialogState from "hooks/useDialogState";
import useCoursePermissions from "hooks/useCoursePermissions";
import {
  combineSeatMetrics,
  formatSeatsLabel,
  hasSeatAccess,
  isUnlimitedSeats,
} from "utils/seatQuotaDisplay";

function CourseDetail(): React.JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentCourse, enrollInCourse } = useCourse();
  const dispatch = useDispatch();
  const [pageLoading, setPageLoading] = React.useState(true);
  const {
    isAdmin,
    isCompanyAdmin,
    isFellowAdmin,
    isCompany,
    isManager,
    isHR,
    isSupervisor,
    isEmployee,
    isCompanyEmployee,
    isCreator,
    isRestrictedEmployee,
    user,
  } = useAuth();
  const { showAlert } = useContext(AppContext);
  const [enrolling, setEnrolling] = React.useState(false);
  const [isSaved, setIsSaved] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [isEnrolled, setIsEnrolled] = React.useState(false);
  const [unenrolling, setUnenrolling] = React.useState(false);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = React.useState(false);
  const [stripeDialogOpen, setStripeDialogOpen] = React.useState(false);
  const [pendingPurchase, setPendingPurchase] = React.useState<{ quota: number; price: number } | null>(null);
  const [hasPurchasedAccess, setHasPurchasedAccess] = React.useState(false);
  const [purchasedQuotaInfo, setPurchasedQuotaInfo] = React.useState<{ quotaPurchased: number; quotaUsed: number; quotaAvailable: number } | null>(null);
  const [freeAssignmentQuotaInfo, setFreeAssignmentQuotaInfo] = React.useState<{ quota: number; quotaUsed: number; quotaAvailable: number } | null>(null);
  const [cloning, setCloning] = useState(false);
  const {
    open: assignmentDialogOpen,
    onOpen: openAssignmentDialog,
    onClose: closeAssignmentDialog,
    setOpen: setAssignmentDialogOpen,
  } = useDialogState(false);
  const {
    canEnrollByRole,
    canAssignCourseForCourse,
    canEditCourse,
    hasOwnedCourseAccess,
  } = useCoursePermissions({
    user,
    isAdmin,
    isCompany,
    isCompanyAdmin,
    isFellowAdmin,
    isCreator,
    isCompanyEmployee,
    isEmployee,
    isManager,
    isHR,
    isSupervisor,
    isRestrictedEmployee,
  });

  useEffect(() => {
    if (!id) {
      setPageLoading(false);
      return;
    }

    setPageLoading(true);
    dispatch(clearCurrentCourse());

    // Fetch course with flag to prevent 401 auto-logout for role-restricted endpoints
    const fetchCourseWithErrorHandling = async () => {
      try {
        const response = await courseApi.get(`/api/courses/${id}`, {
          skipAutoLogout: true,
        } as any);
        // Manually update Redux state with the course data
        const raw = response.data.data || response.data;
        const courseData = {
          ...raw,
          addedByUserID: raw?.addedByUserID ?? raw?.AddedByUserID ?? undefined,
          addedByUserName: raw?.addedByUserName ?? raw?.AddedByUserName ?? undefined,
        };
        dispatch(setCurrentCourse(courseData));
        const enrollmentStatus = response.data.enrollmentStatus;
        setIsEnrolled(Boolean(enrollmentStatus && (enrollmentStatus === 'active' || enrollmentStatus === 'completed' || enrollmentStatus === 'enrolled')));
        if (courseData?.purchaseInfo) {
          setHasPurchasedAccess(Boolean(courseData.purchaseInfo.hasAccess));
          setPurchasedQuotaInfo({
            quotaPurchased: Number(courseData.purchaseInfo.quotaPurchased || 0),
            quotaUsed: Number(courseData.purchaseInfo.quotaUsed || 0),
            quotaAvailable: Number(courseData.purchaseInfo.quotaAvailable || 0),
          });
        } else {
          setHasPurchasedAccess(false);
          setPurchasedQuotaInfo(null);
        }
        if (courseData?.assignmentIsFreeAccess && courseData?.freeAssignmentInfo) {
          setFreeAssignmentQuotaInfo({
            quota: Number(courseData.freeAssignmentInfo.quota || 0),
            quotaUsed: Number(courseData.freeAssignmentInfo.quotaUsed || 0),
            quotaAvailable: Number(courseData.freeAssignmentInfo.quotaAvailable || 0),
          });
        } else {
          setFreeAssignmentQuotaInfo(null);
        }
      } catch (error: any) {
        // Silently handle 401/403 for role-restricted endpoints
        if (error.response?.status === 401 || error.response?.status === 403) {
          return;
        }
        console.error('Error fetching course:', error);
      } finally {
        setPageLoading(false);
      }
    };

    fetchCourseWithErrorHandling();

    checkIfSaved();

  }, [id, dispatch, isEmployee, isCompanyEmployee]);

  const checkIfSaved = async () => {
    if (!id) return;
    try {
      const response = await courseApi.get("/api/courses/saved");
      if (response.data.status) {
        const saved = response.data.data.some((s: any) => s.courseID === id);
        setIsSaved(saved);
      }
    } catch (error) {
      // Ignore error
    }
  };

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      if (isSaved) {
        await courseApi.delete(`/api/courses/save/${id}`);
        setIsSaved(false);
        showAlert("Course removed from library", "success", "Success");
      } else {
        await courseApi.post("/api/courses/save", { courseID: id });
        setIsSaved(true);
        showAlert("Course saved to library", "success", "Success");
      }
    } catch (error: any) {
      showAlert(error.response?.data?.message || "Failed to save course", "error", "Error");
      // Refresh the saved status on error
      checkIfSaved();
    } finally {
      setSaving(false);
    }
  };

  const handleEnroll = async () => {
    if (!id) return;
    if (!canEnroll) {
      showAlert("Your role cannot enroll in courses.", "error", "Error");
      return;
    }
    if (isPaidCourse && !hasAnyAccess) {
      showAlert("Please purchase this course before enrolling.", "error", "Error");
      return;
    }
    setEnrolling(true);
    try {
      await enrollInCourse(id);
      setIsEnrolled(true);
    } catch (error: any) {
      console.error('Enrollment failed:', error);
    } finally {
      setEnrolling(false);
    }
    // No need to call getCourse here, Redux will update state
  };

  const handlePurchaseProceed = (quota: number, price: number) => {
    setPendingPurchase({ quota, price });
    setPurchaseDialogOpen(false);
    setStripeDialogOpen(true);
  };

  const handleUnenroll = async () => {
    if (!id) return;
    const confirmed = await DeleteAlert("Your progress will be lost. Are you sure you want to unenroll from this course?");
    if (!confirmed) return;
    
    setUnenrolling(true);
    try {
      await courseApi.delete(`/api/courses/${id}/unenroll`);
      setIsEnrolled(false);
      showAlert("Successfully unenrolled from course", "success", "Success");
    } catch (error: any) {
      showAlert(error.response?.data?.message || "Failed to unenroll from course", "error", "Error");
    } finally {
      setUnenrolling(false);
    }
  };

  const canEnroll = canEnrollByRole;
  const canAssignCurrentCourse = canAssignCourseForCourse(currentCourse);
  const canEdit = canEditCourse(currentCourse);
  const hasOwnedAccess = hasOwnedCourseAccess(currentCourse);
  const hasFreeAssignmentAccess = currentCourse?.assignmentIsFreeAccess === true;
  const hasAssignmentAccess =
    currentCourse?.visibilitySource === "assigned_to_me" ||
    currentCourse?.visibilitySource === "assigned_to_branch" ||
    currentCourse?.visibilitySource === "assigned_to_company";
  const isPaidCourse = Boolean(!currentCourse?.isFree && (currentCourse?.price ?? 0) > 0);
  // For paid courses assigned without free access, do not grant automatic access; require purchase
  const hasValidAssignmentAccess = hasAssignmentAccess && (!isPaidCourse || hasFreeAssignmentAccess);
  const hasAnyAccess = isAdmin || hasOwnedAccess || hasFreeAssignmentAccess || hasValidAssignmentAccess || hasPurchasedAccess;
  const canShowEnrollButton = canEnroll && (!isPaidCourse || hasAnyAccess);
  const shouldShowPurchaseButton = Boolean(
      isPaidCourse &&
      !isAdmin &&
      !hasOwnedAccess &&
      !hasFreeAssignmentAccess &&
      !hasValidAssignmentAccess &&
      !hasPurchasedAccess
  );
  const isManagerOrHrWithoutBranch = (isManager || isHR) && !user?.branchID;
  const blockedAssignUntilPurchase = Boolean(
    isPaidCourse &&
    hasAssignmentAccess &&
    !hasFreeAssignmentAccess &&
    !hasPurchasedAccess &&
    (isCompany || isManagerOrHrWithoutBranch)
  );
  const canOpenAssignmentDialog = canAssignCurrentCourse && !blockedAssignUntilPurchase;

  useEffect(() => {
    if (searchParams.get("assign") !== "true" || !currentCourse) {
      return;
    }
    if (canOpenAssignmentDialog) {
      setAssignmentDialogOpen(true);
    } else if (blockedAssignUntilPurchase) {
      showAlert("Purchase this course before assigning it to employees or branches.", "warning", "Purchase required");
    }
    setSearchParams({});
  }, [
    searchParams,
    setSearchParams,
    currentCourse,
    canOpenAssignmentDialog,
    blockedAssignUntilPurchase,
    showAlert,
    setAssignmentDialogOpen,
  ]);

  const seatSummary = React.useMemo(
    () =>
      combineSeatMetrics({
        purchase: purchasedQuotaInfo ?? undefined,
        hasPurchaseAccess: hasPurchasedAccess,
        free: freeAssignmentQuotaInfo ?? undefined,
        assignmentIsFreeAccess: currentCourse?.assignmentIsFreeAccess === true,
      }),
    [purchasedQuotaInfo, hasPurchasedAccess, freeAssignmentQuotaInfo, currentCourse?.assignmentIsFreeAccess]
  );

  const isDevMode = process.env.REACT_APP_ENV === 'dev';

  const sortedModules = React.useMemo(() => {
    const mods = currentCourse?.modules ?? [];
    return [...mods].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [currentCourse?.modules]);

  const formatCoursePriceLabel = React.useCallback((course: NonNullable<typeof currentCourse>) => {
    if (course.isFree || !course.price || Number(course.price) <= 0) {
      return "Free";
    }
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(course.price));
  }, []);

  const handleClone = async () => {
    if (!id) return;
    setCloning(true);
    try {
      const response = await courseApi.post(`/api/courses/${id}/clone`);
      const cloned = response.data.data;
      showAlert('Course cloned successfully', 'success', 'Success');
      navigate(`/courses/${cloned.id}/edit`);
    } catch (error: any) {
      showAlert(error.response?.data?.message || 'Failed to clone course', 'error', 'Error');
    } finally {
      setCloning(false);
    }
  };

  const showSeatBadges = Boolean(seatSummary && (isCompany || isManager || isHR));

  const seatQuotaChips = showSeatBadges ? (
    <>
      <Chip
        label={`Total Available: ${formatSeatsLabel(seatSummary!.total)}`}
        color="info"
        size="small"
        variant="outlined"
      />
      <Chip
        label={`Seats Used: ${seatSummary!.used}`}
        color="info"
        size="small"
        variant="outlined"
      />
      <Chip
        label={`Total Remaining: ${formatSeatsLabel(seatSummary!.remaining)}`}
        color={hasSeatAccess(seatSummary!.remaining) ? "success" : "error"}
        size="small"
        variant="outlined"
      />
    </>
  ) : null;

  const fullWidthButtonSx = { width: "100%", justifyContent: "center" } as const;

  const assignCourseActions = (fullWidth = false) => (
    <>
      {canAssignCurrentCourse && blockedAssignUntilPurchase && (
        <Tooltip title="Purchase this course before assigning it to employees or branches.">
          <Box
            component="span"
            sx={fullWidth ? { display: "block", width: "100%" } : undefined}
          >
            <Button
              variant="outlined"
              color="success"
              size="small"
              disabled
              fullWidth={fullWidth}
              sx={{ pointerEvents: "none", ...(fullWidth ? fullWidthButtonSx : {}) }}
            >
              <Assignment sx={{ mr: 1 }} /> Assign Course
            </Button>
          </Box>
        </Tooltip>
      )}
      {canOpenAssignmentDialog && (
        <Button
          variant="outlined"
          color="success"
          size="small"
          fullWidth={fullWidth}
          sx={fullWidth ? fullWidthButtonSx : undefined}
          onClick={openAssignmentDialog}
        >
          <Assignment sx={{ mr: 1 }} /> Assign Course
        </Button>
      )}
    </>
  );

  const editCloneActions = (
    <>
      {canEdit && (
        <Button
          variant="gradient"
          color="info"
          size="small"
          onClick={() => navigate(`/courses/${id}/edit`)}
        >
          <Edit sx={{ mr: 1 }} /> Edit Course
        </Button>
      )}
      {/* {isDevMode && (
        <Button
          variant="outlined"
          color="warning"
          size="small"
          onClick={handleClone}
          loading={cloning}
          disabled={cloning}
        >
          <ContentCopy sx={{ mr: 1 }} /> Clone Course
        </Button>
      )} */}
    </>
  );

  if (pageLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box py={6} display="flex" justifyContent="center" minHeight="40vh">
          <Loader loading={true} />
        </Box>
        <Footer />
      </DashboardLayout>
    );
  }

  if (!currentCourse || String(currentCourse.id) !== String(id)) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box py={3}>
          <Typography variant="h6" color="error">
            Course not found
          </Typography>
          <Button
            variant="outlined"
            color="info"
            onClick={() => navigate("/courses")}
            sx={{ mt: 2 }}
          >
            <ArrowBack sx={{ mr: 1 }} /> Back to Courses
          </Button>
        </Box>
        <Footer />
      </DashboardLayout>
    );
  }
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3} {...({} as any)}>
        <Button
          variant="text"
          color="info"
          onClick={() => navigate("/courses")}
          {...({ sx: { mb: 2 } } as any)}
        >
          <ArrowBack sx={{ mr: 1 }} /> Back to Courses
        </Button>

        <Card>
          <Box p={3} {...({} as any)}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", lg: "start" },
                mb: 2,
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                <Box
                  sx={{
                    display: { xs: "flex", lg: "none" },
                    justifyContent: "flex-end",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  {editCloneActions}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", lg: "row" },
                    alignItems: { xs: "flex-start", lg: "center" },
                    gap: { xs: 1, lg: 2 },
                    mb: 1.5,
                  }}
                >
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    sx={{ width: { xs: "100%", sm: "100%", md: "100%", lg: "auto" } }}
                  >
                    {currentCourse.title}
                  </Typography>
                  <CourseStatusChip status={currentCourse.status} size="small" />
                  {showSeatBadges && (
                    <Box
                      sx={{
                        display: { xs: "none", lg: "flex" },
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      {seatQuotaChips}
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{
                    display: { xs: "none", sm: "none", md: "flex", lg: "none" },
                    justifyContent: "flex-end",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  {assignCourseActions()}
                </Box>

                {showSeatBadges && (
                  <Box
                    sx={{
                      display: { xs: "flex", lg: "none" },
                      flexWrap: "wrap",
                      width: "100%",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    {seatQuotaChips}
                  </Box>
                )}

                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} alignItems="center">
                  <Chip
                    label={formatCoursePriceLabel(currentCourse)}
                    color={currentCourse.isFree || !currentCourse.price || Number(currentCourse.price) <= 0 ? "success" : "info"}
                    size="small"
                    variant={currentCourse.isFree || !currentCourse.price || Number(currentCourse.price) <= 0 ? "filled" : "outlined"}
                  />
                  {(currentCourse.addedByUserName || currentCourse.addedByUserID) && (
                    <Chip
                      icon={<PersonOutline sx={{ "&&": { fontSize: 18 } }} />}
                      label={
                        currentCourse.addedByUserName
                          ? `Added by ${currentCourse.addedByUserName}`
                          : currentCourse.addedByUserID
                          ? `Added by (id: ${currentCourse.addedByUserID})`
                          : "Added by"
                      }
                      size="small"
                      variant="outlined"
                      color="secondary"
                    />
                  )}
                  {currentCourse.instructorName && (
                    <Chip
                      icon={<School sx={{ "&&": { fontSize: 18 } }} />}
                      label={`Instructor: ${currentCourse.instructorName}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>

              <Box
                sx={{
                  display: { xs: "none", lg: "flex" },
                  gap: 1,
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                }}
              >
                {editCloneActions}
                {assignCourseActions()}
              </Box>

            </Box>

            {currentCourse.description && (
              <Typography variant="body1" color="text" mb={3} {...({} as any)}>
                {currentCourse.description}
              </Typography>
            )}

            {sortedModules.length > 0 && (
              <Box sx={{ mb: 2 }} {...({} as any)}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ display: "block", letterSpacing: 1.2, mb: 1, fontWeight: 600 }}
                  {...({} as any)}
                >
                  Course outline
                </Typography>
                <Paper
                  variant="outlined"
                  elevation={0}
                  sx={(theme) => ({
                    borderRadius: 1.5,
                    overflow: "hidden",
                    border: '0px',
                    boxShadow:'0px 0px 10px rgba(0, 0, 0, 0.23)'
                  })}
                >
                  {sortedModules.map((module, modIndex) => {
                    const lessons = [...(module.content ?? [])].sort(
                      (a, b) => (a.order ?? 0) - (b.order ?? 0)
                    );
                    const typeLabel = (t: string | undefined) =>
                      t ? String(t).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "";
                    return (
                      <Accordion
                        key={module.id ?? `mod-${modIndex}`}
                        disableGutters
                        elevation={0}
                        defaultExpanded={modIndex === 0}
                        sx={(theme) => ({
                          "&:before": { display: "none" },
                          backgroundColor: "transparent",
                          borderBottom:
                            modIndex < sortedModules.length - 1
                              ? `1px solid ${alpha(theme.palette.divider, 0.15)}`
                              : "none",
                          "&:last-of-type": { borderBottom: "none" },
                        })}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMore sx={{ fontSize: 20, color: "text.secondary" }} />}
                          sx={{
                            minHeight: 40,
                            px: 1.5,
                            py: 0,
                            "& .MuiAccordionSummary-content": { my: 0.75, alignItems: "center" },
                          }}
                        >
                          <Stack direction="row" spacing={1.25} alignItems="center" width="100%" minWidth={0}>
                            <Typography
                              variant="caption"
                              sx={(theme) => ({
                                fontVariantNumeric: "tabular-nums",
                                width: 22,
                                flexShrink: 0,
                                fontWeight: 600,
                                color: theme.palette.text.disabled,
                              })}
                              {...({} as any)}
                            >
                              {modIndex + 1}
                            </Typography>
                            <Box flex={1} minWidth={0}>
                              <Typography variant="body2" fontWeight={600} noWrap title={module.title} {...({} as any)}>
                                {module.title}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }} {...({} as any)}>
                                {lessons.length} lesson{lessons.length === 1 ? "" : "s"}
                              </Typography>
                            </Box>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails
                          sx={(theme) => ({
                            px: 1.5,
                            pt: 1.25,
                            pb: 1.25,
                            pl: 7.25,
                            bgcolor: '',
                            border:'1px solid rgba(0,0,0,0.1)'
                          })}
                        >
                          {module.description ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: "block",
                                mb: 1,
                                whiteSpace: "pre-wrap",
                                lineHeight: 1.45,
                                maxHeight: "4.5em",
                                overflow: "hidden",
                              }}
                              {...({} as any)}
                            >
                              {module.description}
                            </Typography>
                          ) : null}
                          {lessons.length > 0 ? (
                            <Stack component="ul" spacing={0.35} sx={{ m: 0, pl: 1.25, listStyleType: "disc" }}>
                              {lessons.map((lesson, li) => (
                                <Typography
                                  component="li"
                                  key={lesson.id ?? `lesson-${modIndex}-${li}`}
                                  variant="caption"
                                  color="text.primary"
                                  sx={{
                                    display: "list-item",
                                    pl: 0.25,
                                    lineHeight: 1.35,
                                    backgroundColor:'white',
                                    fontWeight: 500,
                                    "&::marker": { color: "text.secondary" },
                                  }}
                                  {...({} as any)}
                                >
                                  {lesson.title}
                                  {lesson.type ? (
                                    <Box component="span" sx={{ color: "text.secondary", fontWeight: 400, ml: 0.5 }}>
                                      · {typeLabel(lesson.type)}
                                    </Box>
                                  ) : null}
                                </Typography>
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary" {...({} as any)}>
                              No lessons yet.
                            </Typography>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Paper>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {currentCourse.content && (
              <Box mb={3} {...({} as any)}>
                <Typography variant="h6" fontWeight="bold" mb={1.5} {...({} as any)}>
                  Course Content
                </Typography>
                <Box
                  sx={(theme) => ({
                    py: 2,
                    px: 2.5,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.35 : 0.65)}`,
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.common.white, 0.04)
                        : alpha(theme.palette.background.paper, 1),
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "none"
                        : `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`,
                    whiteSpace: "pre-wrap",
                  })}
                >
                  <Typography
                    variant="body2"
                    component="div"
                    sx={(theme) => ({
                      color: theme.palette.text.primary,
                      lineHeight: 1.65,
                      m: 0,
                    })}
                    {...({} as any)}
                  >
                    {currentCourse.content}
                  </Typography>
                </Box>
              </Box>
            )}

            {((canAssignCurrentCourse && blockedAssignUntilPurchase) ||
              canOpenAssignmentDialog ||
              currentCourse.status === "published") && (
              <Box
                sx={{
                  mt: 3,
                  display: { xs: "flex", sm: "flex", md: "none" },
                  flexDirection: "column",
                  gap: 1.5,
                  width: "100%",
                }}
              >
                {currentCourse.status === "published" && (
                  <>
                    {canShowEnrollButton && !isEnrolled && (
                      <Button
                        variant="gradient"
                        color="info"
                        size="small"
                        fullWidth
                        sx={fullWidthButtonSx}
                        onClick={handleEnroll}
                        loading={enrolling}
                        disabled={enrolling}
                      >
                        <School sx={{ mr: 1 }} /> Enroll in Course
                      </Button>
                    )}
                    {isEnrolled && (
                      <Button
                        variant="gradient"
                        color="info"
                        size="small"
                        fullWidth
                        sx={fullWidthButtonSx}
                        onClick={() => navigate(`/courses/${id}/play`)}
                      >
                        <PlayArrow sx={{ mr: 1 }} /> Continue watching
                      </Button>
                    )}
                    {!isEnrolled && shouldShowPurchaseButton && (
                      <Button
                        variant="gradient"
                        color="warning"
                        size="small"
                        fullWidth
                        sx={fullWidthButtonSx}
                        onClick={() => setPurchaseDialogOpen(true)}
                      >
                        <ShoppingCart sx={{ mr: 1 }} /> Purchase Course
                      </Button>
                    )}
                    {!isEnrolled && currentCourse.modules && currentCourse.modules.length > 0 && hasAnyAccess && !shouldShowPurchaseButton && (
                      <Button
                        variant="outlined"
                        color="success"
                        size="small"
                        fullWidth
                        sx={fullWidthButtonSx}
                        onClick={() => navigate(`/courses/${id}/play`)}
                      >
                        <PlayArrow sx={{ mr: 1 }} /> Watch Course
                      </Button>
                    )}
                  </>
                )}
                {assignCourseActions(true)}
                {currentCourse.status === "published" && (
                  <>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      fullWidth
                      sx={fullWidthButtonSx}
                      onClick={handleSave}
                      loading={saving}
                      disabled={saving}
                    >
                      {isSaved ? <Bookmark sx={{ mr: 1 }} /> : <BookmarkBorder sx={{ mr: 1 }} />}
                      {isSaved ? "Remove from library" : "Save"}
                    </Button>
                    {isEnrolled && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        fullWidth
                        sx={fullWidthButtonSx}
                        onClick={handleUnenroll}
                        loading={unenrolling}
                        disabled={unenrolling}
                      >
                        <LogoutOutlined sx={{ mr: 1 }} /> Unenroll
                      </Button>
                    )}
                  </>
                )}
              </Box>
            )}

            {currentCourse.status === "published" && (
              <Box
                mt={3}
                sx={{
                  display: { xs: "none", sm: "none", md: "flex" },
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                {canShowEnrollButton && !isEnrolled && (
                  <Button
                    variant="gradient"
                    color="info"
                    size="small"
                    onClick={handleEnroll}
                    loading={enrolling}
                    disabled={enrolling}
                  >
                    <School sx={{ mr: 1 }} /> Enroll in Course
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={handleSave}
                  loading={saving}
                  disabled={saving}
                >
                  {isSaved ? <Bookmark sx={{ mr: 1 }} /> : <BookmarkBorder sx={{ mr: 1 }} />}
                  {isSaved ? "Remove from library" : "Save"}
                </Button>
                {isEnrolled && (
                  <Button
                    variant="gradient"
                    color="info"
                    size="small"
                    onClick={() => navigate(`/courses/${id}/play`)}
                  >
                    <PlayArrow sx={{ mr: 1 }} /> Continue watching
                  </Button>
                )}
                {!isEnrolled && shouldShowPurchaseButton && (
                  <Button
                    variant="gradient"
                    color="warning"
                    size="small"
                    onClick={() => setPurchaseDialogOpen(true)}
                  >
                    <ShoppingCart sx={{ mr: 1 }} /> Purchase Course
                  </Button>
                )}
                {!isEnrolled && currentCourse.modules && currentCourse.modules.length > 0 && hasAnyAccess && !shouldShowPurchaseButton && (
                  <Button
                    variant="outlined"
                    color="success"
                    size="small"
                    onClick={() => navigate(`/courses/${id}/play`)}
                  >
                    <PlayArrow sx={{ mr: 1 }} /> Watch Course
                  </Button>
                )}
                {isEnrolled && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleUnenroll}
                    loading={unenrolling}
                    disabled={unenrolling}
                  >
                    <LogoutOutlined sx={{ mr: 1 }} /> Unenroll
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Card>
      </Box>
      <Footer />
      
      {canOpenAssignmentDialog && currentCourse && (
        <CourseAssignmentDialog
          open={assignmentDialogOpen}
          onClose={closeAssignmentDialog}
          courseID={id || ''}
          courseTitle={currentCourse.title}
          courseCompanyID={currentCourse.companyID}
          courseAddedByUserID={currentCourse.addedByUserID}
          courseInstructorID={currentCourse.instructorID}
          isPaidCourse={isPaidCourse}
          availablePurchasedSeats={
            purchasedQuotaInfo != null ? Number(purchasedQuotaInfo.quotaAvailable) : undefined
          }
          onSuccess={() => {
            // Refresh course data if needed
          }}
        />
      )}

      {currentCourse && (
        <CoursePurchaseDialog
          open={purchaseDialogOpen}
          onClose={() => setPurchaseDialogOpen(false)}
          courseTitle={currentCourse.title}
          quotaPricing={currentCourse.quotaPricing ?? {}}
          baseCoursePrice={Number(currentCourse.price ?? 0)}
          onProceed={handlePurchaseProceed}
        />
      )}

      {currentCourse && pendingPurchase && (
        <StripePaymentDialog
          open={stripeDialogOpen}
          onClose={() => setStripeDialogOpen(false)}
          courseID={id!}
          courseTitle={currentCourse.title}
          quota={pendingPurchase.quota}
          price={pendingPurchase.price}
          onPaymentComplete={() => {
            setStripeDialogOpen(false);
            setHasPurchasedAccess(true);
            if (pendingPurchase) {
              setPurchasedQuotaInfo((prev) => {
                const hasUnlimited = isUnlimitedSeats(prev?.quotaPurchased) || isUnlimitedSeats(prev?.quotaAvailable);
                const quotaPurchased = hasUnlimited ? -1 : Number(prev?.quotaPurchased || 0) + pendingPurchase.quota;
                const quotaUsed = Number(prev?.quotaUsed || 0);
                return {
                  quotaPurchased,
                  quotaUsed,
                  quotaAvailable: hasUnlimited ? -1 : Math.max(0, quotaPurchased - quotaUsed),
                };
              });
            }
            showAlert("Purchase successful! You now have access to this course.", "success", "Success");
          }}
        />
      )}
    </DashboardLayout>
  );
}

export default CourseDetail;

